import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, ShoppingBag, FileText, Clock, Megaphone, Cake, ChevronRight } from "lucide-react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { onPosterError } from "@/lib/neutralPoster";

const isExpired = (ref) => !!ref?.expires_at && new Date(ref.expires_at).getTime() < Date.now();
const fmtPrice = (p, c) => (p == null ? "" : `${Number(p).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c === "USD" ? "$" : (c || "")}`.trim());

// Thumbnail of the referenced object: story media / text plate, listing photo, post image.
const RefThumb = ({ card: r, size }) => {
  const style = { width: size, height: Math.round(size * 1.3) };
  const img = r.type === "story" ? (r.media_type === "video" ? (r.poster_url || null) : r.media_url) : r.image_url;
  if (r.type === "story" && r.media_type === "text") {
    return (
      <span className="shrink-0 rounded-md overflow-hidden grid place-items-center px-1 text-center text-white font-semibold" style={{ ...style, background: r.bg || "linear-gradient(135deg,#D97757,#8A5A12)", fontSize: 8, lineHeight: 1.15 }}>
        <span className="line-clamp-3">{r.text}</span>
      </span>
    );
  }
  if (r.type === "birthday") {
    return (
      <span className="shrink-0 rounded-md grid place-items-center" style={{ width: size, height: size, background: "var(--yalola-primary-soft)" }}>
        <Cake size={Math.round(size * 0.5)} style={{ color: "var(--yalola-primary)" }} />
      </span>
    );
  }
  return (
    <span className="relative shrink-0 rounded-md overflow-hidden bg-black/80 grid place-items-center" style={style}>
      {img ? <img src={resolveMediaUrl(img)} alt="" onError={onPosterError} className="absolute inset-0 w-full h-full object-cover" /> : null}
      {r.type === "story" && r.media_type === "video" && <Play size={14} className="relative text-white" fill="white" />}
      {!img && r.type === "listing" && <ShoppingBag size={16} className="relative text-white/80" />}
      {!img && r.type === "ad" && <Megaphone size={16} className="relative text-white/80" />}
      {!img && r.type === "post" && <FileText size={16} className="relative text-white/80" />}
    </span>
  );
};

export const refTitle = (r, { user, chatTargetName, t }) => {
  if (r.type === "story") {
    return r.author_id === user?.id
      ? t("chat.ref_your_story", "Votre story")
      : t("chat.ref_their_story", "Story de {name}").replace("{name}", chatTargetName || "");
  }
  if (r.type === "listing") return r.title || t("chat.ref_listing", "Annonce");
  // Facebook Messenger-style « Vous avez répondu à la publicité : … » — the advertiser knows WHICH ad the member means.
  if (r.type === "ad") return t("chat.ref_ad", "Publicité : {title}").replace("{title}", r.title || "");
  if (r.type === "birthday") return t("chat.ref_birthday", "Vœux d'anniversaire");
  return t("chat.ref_post", "Publication");
};

export const refSubtitle = (r, t) => {
  if (r.type === "story") {
    if (isExpired(r)) return t("chat.ref_story_expired", "Story expirée");
    return r.text || (r.media_type === "video" ? t("chat.ref_video", "Vidéo") : t("chat.ref_photo", "Photo"));
  }
  if (r.type === "listing") return fmtPrice(r.price, r.currency) || t("chat.ref_listing", "Annonce");
  if (r.type === "ad") return r.text || t("chat.ref_ad_sub", "Message envoyé depuis le bouton de la publicité");
  if (r.type === "birthday") return t("chat.ref_birthday_sub", "Envoyé depuis « Anniversaires du jour »");
  return r.text || "";
};

// Where a tap on the card leads (null = not clickable). Ads: the ad's post, else the advertiser's site.
export const refTarget = (r) => {
  if (!r) return null;
  if (r.type === "ad") return r.link ? { to: r.link } : (r.external_url ? { href: r.external_url } : null);
  if (r.type === "birthday") return null;
  return r.link ? { to: r.link } : null;
};

// Rich, clickable ad card (link-preview style): 16:9 visual, « Sponsorisé », title, text, « Voir la publicité ».
const AdRefCard = ({ card: r, mine, t, onOpen, testId }) => {
  const target = refTarget(r);
  return (
    <button
      type="button"
      onClick={onOpen}
      onPointerDown={(e) => e.stopPropagation()}
      className="block w-full text-left rounded-xl overflow-hidden mb-1 border transition-transform active:scale-[0.99]"
      style={{ width: 240, maxWidth: "100%", background: mine ? "rgba(255,255,255,0.65)" : "var(--yalola-surface)", borderColor: "var(--yalola-border)", cursor: target ? "pointer" : "default" }}
      data-testid={testId || `chat-ref-ad-${r.id}`}
      data-rich="ad"
      data-clickable={target ? "1" : "0"}
    >
      {r.image_url && (
        <span className="block relative w-full bg-black/80" style={{ aspectRatio: "16 / 9" }}>
          <img src={resolveMediaUrl(r.image_url)} alt="" onError={onPosterError} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId || `chat-ref-ad-${r.id}`}-image`} />
        </span>
      )}
      <span className="block px-2.5 py-2">
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-primary)" }}>
          <Megaphone size={11} /> {t("chat.ref_ad_sponsored", "Sponsorisé")}
        </span>
        {r.title && <span className="block text-[13px] font-semibold leading-snug line-clamp-2 mt-0.5">{r.title}</span>}
        {r.text && <span className="block text-[11px] leading-snug line-clamp-2 mt-0.5" style={{ opacity: 0.8 }}>{r.text}</span>}
        {target && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 mt-2" style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid={`${testId || `chat-ref-ad-${r.id}`}-open`}>
            {t("chat.ref_ad_open", "Voir la publicité")} <ChevronRight size={12} />
          </span>
        )}
      </span>
    </button>
  );
};

// WhatsApp-style context card at the top of a bubble: « Story de X » / a Marketplace
// listing / a shared post / an ad / birthday wishes. Tap → opens the story (if still live), the listing, the post or the ad.
export const RefCard = ({ card: r, mine, user, chatTargetName, t, size = 40, compact = false, testId }) => {
  const navigate = useNavigate();
  if (!r) return null;
  const expired = r.type === "story" && isExpired(r);
  const open = (e) => {
    e.stopPropagation();
    if (r.type === "story") {
      if (expired) { toast(t("chat.ref_story_unavailable", "Cette story n'est plus disponible."), { icon: <Clock size={16} /> }); return; }
      navigate(`/home?story=${encodeURIComponent(r.group_id || r.author_id || "")}&sid=${encodeURIComponent(r.id)}`);
      return;
    }
    const target = refTarget(r);
    if (target?.to) navigate(target.to);
    else if (target?.href) window.open(target.href, "_blank", "noopener");
  };
  if (r.type === "ad" && !compact) return <AdRefCard card={r} mine={mine} t={t} onOpen={open} testId={testId} />;
  return (
    <button
      type="button"
      onClick={open}
      onPointerDown={(e) => e.stopPropagation()}
      className={`flex items-center gap-2 w-full text-left rounded-lg border-l-2 overflow-hidden ${compact ? "px-2 py-1" : "px-2 py-1.5 mb-1"}`}
      style={{ borderColor: "var(--yalola-primary)", background: mine ? "rgba(255,255,255,0.5)" : "var(--yalola-surface-alt)", opacity: expired ? 0.75 : 1, minWidth: compact ? undefined : 200, maxWidth: 280 }}
      data-testid={testId || `chat-ref-${r.type}-${r.id}`}
    >
      <RefThumb card={r} size={size} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold truncate" style={{ color: "var(--yalola-primary)" }}>{refTitle(r, { user, chatTargetName, t })}</div>
        <div className="text-[11px] truncate" style={{ opacity: 0.85 }}>{refSubtitle(r, t)}</div>
      </div>
    </button>
  );
};
